/**
 * Gate del DEBRIEF: ¿esta charla dejó suficiente para abrir el cierre?
 *
 * Vive aparte del controller (puro, testeable sin React) porque su criterio es
 * sutil: cuenta turnos del USER, y las burbujas históricas cuentan o no según
 * de qué FILA de `voice_sessions` vengan.
 */

/**
 * Turnos MÍNIMOS del user (no del coach) para que valga un debrief. Contamos
 * sólo los turnos del user para que el criterio coincida con el del copy del
 * backend (que también mide turnos del user): antes contábamos coach+user y se
 * abría un debrief que después se disculpaba por "sesión corta". Por debajo de
 * esto, salir manda a Home — con un aviso previo si el user habló algo (ver
 * `requestLeave` en el controller).
 */
export const DEBRIEF_MIN_TURNS = 3;

/** Lo mínimo que el gate necesita de una burbuja del hilo. */
export interface GateMessage {
  who: string;
  historical?: boolean;
}

/**
 * ¿Las burbujas `historical` del hilo pertenecen a la MISMA fila de
 * `voice_sessions` que la charla viva?
 *
 * Al entrar sembramos el transcript previo como `historical` en DOS casos que
 * terminan distinto en el backend:
 *  - **reattach tras F5** (el reload perdió todo lo in-memory): el backend
 *    REUSA la fila si sigue siendo elegible → el `sessionId` vivo es el MISMO
 *    que mandamos como reconnect, y el debrief de esa sesión ya incluye esos
 *    turnos (`getSessionTurnCount` los ve). Cuentan.
 *  - **"Continuar conversación"** (o un reattach que el backend rechazó por
 *    gracia vencida / fila ya distilada): crea una fila HIJA con id NUEVO. El
 *    debrief es del fragmento nuevo, así que el historial NO cuenta.
 *
 * La igualdad de ids es justamente la señal de "misma fila", sin inventar un
 * flag nuevo en el protocolo.
 */
export function isSameRowHistory(
  liveSessionId: string | null | undefined,
  seededSessionId: string | null | undefined,
): boolean {
  return liveSessionId != null && seededSessionId != null && liveSessionId === seededSessionId;
}

/**
 * Turnos del user que cuentan para el gate del debrief (y para el aviso de
 * salida corta). Excluye siempre al coach; incluye los históricos sólo cuando
 * son de la misma fila (ver {@link isSameRowHistory}).
 */
export function countGateUserTurns(
  messages: ReadonlyArray<GateMessage>,
  opts: { sameRowHistory: boolean },
): number {
  return messages.filter((m) => m.who === 'user' && (opts.sameRowHistory || !m.historical)).length;
}

/**
 * Burbujas que cuentan como "esta charla tuvo contenido" (coach + user). Sólo
 * decide si RECORDAR la sesión para el "Seguir la charla" del player de
 * Escuchar; mismo criterio de históricos que el gate.
 */
export function countGateTurns(
  messages: ReadonlyArray<GateMessage>,
  opts: { sameRowHistory: boolean },
): number {
  return messages.filter((m) => opts.sameRowHistory || !m.historical).length;
}
